import React from 'react'

import { InputGroup, Checkbox, DropdownGroup, Flex } from '@clear/design-system'
import { CountryOption } from '@api/getCountries'
import {
  BiographicSearchKeys,
  BiographicSearchLabels,
} from '@validators/schema/shared/biographicSearchSchema'
import { handleDataChange } from '@utils/handleDateChange'
import prependBlankOption from '@utils/prependBlankOption'
import { Path, UseFormRegister, UseFormSetValue } from 'react-hook-form'

type RegisteredKey =
  typeof BiographicSearchKeys[keyof typeof BiographicSearchKeys]

type CommonFieldState = Omit<
  Record<RegisteredKey, Partial<{ errorMessage: string; disabled: boolean }>>,
  'noMiddleName' | 'suffix'
> & {
  forceDisable: boolean
}

type Props<T extends Partial<Record<string, unknown>>> = {
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>

  state: CommonFieldState
  countries: CountryOption[]
}

function BiographicsStatusSearchView<
  FormFields extends Partial<Record<string, unknown>>
>({
  register,
  setValue,

  countries,
  state,
}: Props<FormFields>) {
  return (
    <Flex flexDirection="column">
      <InputGroup
        {...register(BiographicSearchKeys.FIRST_NAME as Path<FormFields>)}
        id={BiographicSearchKeys.FIRST_NAME}
        data-cy={BiographicSearchKeys.FIRST_NAME}
        name={BiographicSearchKeys.FIRST_NAME}
        label={BiographicSearchLabels.firstName}
        disabled={state.forceDisable}
        error={state.firstName.errorMessage}
      />
      <InputGroup
        {...register(BiographicSearchKeys.MIDDLE_NAME as Path<FormFields>)}
        id={BiographicSearchKeys.MIDDLE_NAME}
        data-cy={BiographicSearchKeys.MIDDLE_NAME}
        name={BiographicSearchKeys.MIDDLE_NAME}
        label={BiographicSearchLabels.middleName}
        disabled={state.forceDisable || state.middleName.disabled}
        error={state.middleName.errorMessage}
      />
      <Checkbox
        {...register(BiographicSearchKeys.NO_MIDDLE_NAME as Path<FormFields>)}
        id={BiographicSearchKeys.NO_MIDDLE_NAME}
        data-cy={BiographicSearchKeys.NO_MIDDLE_NAME}
        name={BiographicSearchKeys.NO_MIDDLE_NAME}
        text={BiographicSearchLabels.noMiddleName}
        disabled={state.forceDisable}
        flexShrink={0}
        flexGrow={0}
      />
      <InputGroup
        {...register(BiographicSearchKeys.LAST_NAME as Path<FormFields>)}
        id={BiographicSearchKeys.LAST_NAME}
        data-cy={BiographicSearchKeys.LAST_NAME}
        name={BiographicSearchKeys.LAST_NAME}
        label={BiographicSearchLabels.lastName}
        disabled={state.forceDisable || state.lastName.disabled}
        error={state.lastName.errorMessage}
      />
      <InputGroup
        data-cy="dateOfBirth"
        label="Date of Birth"
        placeholder="mm/dd/yyyy"
        {...register(BiographicSearchKeys.DOB as Path<FormFields>)}
        onChange={(e) =>
          handleDataChange(e, setValue, BiographicSearchKeys.DOB)
        }
        disabled={state.forceDisable || state.dob.disabled}
        error={state.dob.errorMessage}
      />
      <DropdownGroup
        {...register(BiographicSearchKeys.COUNTRY_OF_BIRTH as Path<FormFields>)}
        id={BiographicSearchKeys.COUNTRY_OF_BIRTH}
        name={BiographicSearchKeys.COUNTRY_OF_BIRTH}
        data-cy={BiographicSearchKeys.COUNTRY_OF_BIRTH}
        label={BiographicSearchLabels.countryOfBirth}
        options={prependBlankOption(countries)}
        disabled={state.forceDisable || state.countryOfBirth.disabled}
        error={state.countryOfBirth.errorMessage}
      />
    </Flex>
  )
}

export default BiographicsStatusSearchView
